import { useEffect } from 'react'
import { useUIStore } from '@/store'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import clsx from 'clsx'

const iconMap = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info
}

const colorMap = {
  success: 'bg-status-healthy/10 border-status-healthy/30 text-status-healthy',
  error: 'bg-status-critical/10 border-status-critical/30 text-status-critical',
  warning: 'bg-status-warning/10 border-status-warning/30 text-status-warning',
  info: 'bg-primary-500/10 border-primary-500/30 text-primary-400'
}

export default function ToastContainer() {
  const { notifications, removeNotification } = useUIStore()

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          notification={notification}
          onClose={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  )
}

interface ToastProps {
  notification: {
    id: string
    type: 'success' | 'error' | 'warning' | 'info'
    title: string
    message?: string
    duration?: number
  }
  onClose: () => void
}

function Toast({ notification, onClose }: ToastProps) {
  const Icon = iconMap[notification.type]
  const colorClass = colorMap[notification.type]

  useEffect(() => {
    const duration = notification.duration || 5000
    const timer = setTimeout(() => {
      onClose()
    }, duration)

    return () => clearTimeout(timer)
  }, [notification.duration, onClose])

  return (
    <div
      className={clsx(
        'p-4 rounded-lg border shadow-lg animate-in slide-in-from-right',
        'bg-surface-800',
        colorClass
      )}
    >
      <div className="flex items-start gap-3">
        <Icon size={20} className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-surface-100">{notification.title}</p>
          {notification.message && (
            <p className="text-sm text-surface-400 mt-1">{notification.message}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 p-1 rounded hover:bg-surface-700 transition-colors"
        >
          <X size={16} className="text-surface-400" />
        </button>
      </div>
    </div>
  )
}
